import { required, numeric, minLength, maxLength, helpers } from "@vuelidate/validators";
import { getOrganization, insertOrganization, updateOrganization } from "@/services/organizationLambda";
import { formatPhoneNumber, formatPostalCode, sanitizeNumber, parseLocalDate } from "@/utils/dataUtils";
import { insertOrganizationNote } from "../modules/program/services/programLambda";
import { MODEL_TYPES } from "@/utils/constants";
import { useFieldRequiredValidator } from "@/composables/FieldRequiredValidator";

const requiredFields = [
    { fieldName: 'name', modelType: MODEL_TYPES.Organization },
    { fieldName: 'address1', modelType: MODEL_TYPES.Organization },
    { fieldName: 'address2', modelType: MODEL_TYPES.Organization },
    { fieldName: 'city', modelType: MODEL_TYPES.Organization },
    { fieldName: 'stateProvince', modelType: MODEL_TYPES.Organization },
    { fieldName: 'postalCode', modelType: MODEL_TYPES.Organization },
    { fieldName: 'fiscalYearEnd', modelType: MODEL_TYPES.Organization },
    { fieldName: 'npiNumber', modelType: MODEL_TYPES.Organization },
    { fieldName: 'naicNumber', modelType: MODEL_TYPES.Organization },
    { fieldName: 'countyId', modelType: MODEL_TYPES.Organization },
    { fieldName: 'otherCountyName', modelType: MODEL_TYPES.Organization },
    { fieldName: 'mailAddress1', modelType: MODEL_TYPES.Organization },
    { fieldName: 'mailAddress2', modelType: MODEL_TYPES.Organization },
    { fieldName: 'mailCity', modelType: MODEL_TYPES.Organization },
    { fieldName: 'mailStateProvince', modelType: MODEL_TYPES.Organization },
    { fieldName: 'mailPostalCode', modelType: MODEL_TYPES.Organization },
    { fieldName: 'mailCountyId', modelType: MODEL_TYPES.Organization },
    { fieldName: 'mailOtherCountyName', modelType: MODEL_TYPES.Organization },    
    { fieldName: 'phoneExtension', modelType: MODEL_TYPES.Organization },
    { fieldName: 'fax', modelType: MODEL_TYPES.Organization },
    { fieldName: 'openDate', modelType: MODEL_TYPES.Organization },
    { fieldName: 'closeDate', modelType: MODEL_TYPES.Organization },    
    { fieldName: 'phone', modelType: MODEL_TYPES.Organization },
    { fieldName: 'webUrl', modelType: MODEL_TYPES.Organization },
    { fieldName: 'groupCode', modelType: MODEL_TYPES.Organization },
    { fieldName: 'taxId', modelType: MODEL_TYPES.Organization },
];

const validators = useFieldRequiredValidator(requiredFields);
const { isFieldRequired } = validators;
const requiredValidators = validators.requiredValidators();


/**
 * Validates if a URL is valid.
 * @param {string} value - The URL to validate.
 * @returns {boolean} True if the URL is valid, otherwise false.
 */
const validUrl = (value) => {
    if (!value) {
        return true;
    }

    const urlPattern = /^(https?:\/\/)?((([a-zA-Z0-9$_.+!*'(),;?&=-]|%[0-9a-fA-F]{2})+:)*([a-zA-Z0-9$_.+!*'(),;?&=-]|%[0-9a-fA-F]{2})+@)?(((\d{1,3}\.){3}\d{1,3})|(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}))(:\d{2,5})?(\/([a-zA-Z0-9$_.+!*'(),;:@&=-]|%[0-9a-fA-F]{2})*)*(\?([a-zA-Z0-9$_.+!*'(),;:@&=-]|%[0-9a-fA-F]{2})*)?(#([a-zA-Z0-9$_.+!*'(),;:@&=-]|%[0-9a-fA-F]{2})*)?$/;
    return urlPattern.test(value);

};

const otherCountyRequired = function(value, siblings) {
    const isCountyRequired = isFieldRequired('countyId', siblings);
    if (isCountyRequired && siblings.countyId == 0) {
        const isOtherCountyRequired = isFieldRequired('otherCountyName', siblings);
        const isOtherCountyValid = !requiredValidators['otherCountyName'](value, siblings);
        return (isOtherCountyRequired && !isOtherCountyValid) || !isOtherCountyRequired;
    } 
    
    return true;
};

/**
 * Represents an organization model with various properties and methods.
 */
export class OrganizationModel {
    /**
     * Initializes a new instance of the OrganizationModel class.
     */
    constructor(parentOrganizationType = '') {
        this.organizationId = -1;
        this.hccisId = -1;
        this.parentOrganizationId = -1;
        this.parentOrganizationName = '';
        this.organizationTypeId = null;
        this.organizationTypeDesc = parentOrganizationType;
        this.name = '';
        this.address1 = '';
        this.address2 = '';
        this._city = '';
        this.stateProvince = '';
        this.countyId = null;
        this._countyName = '';
        this._otherCountyName = '';
        this.postalCode = '';
        this.mailAddress1 = '';
        this.mailAddress2 = '';
        this._mailCity = '';
        this.mailStateProvince = '';
        this.mailCountyId = null;
        this._mailCountyName = '';
        this._mailOtherCountyName = '';        
        this.mailPostalCode = '';
        this.phone = '';
        this.phoneExtension = '';
        this.fax = '';
        this.openDate = null;
        this.closeDate = null;
        this.fiscalYearEnd = null;
        this.webUrl = '';
        this.groupCode = '';
        this.taxId = '';
        this.npiNumber = null;
        this.naicNumber = null;
        this.isActive = false;
        this.isInternal = false;
        this.status = '';
        this.isNew = true;
        this.isAffiliate = true;
        this.isRecordLocked = false;
        this.initialFiscalYearEnd = null;
        this.fiscalYearEndChangeExplanation = '';
    }

    /**
     * Gets the city name.
     * @returns {string} The city name.
     */
    get city() {
        return this._city;
    }

    /**
     * Sets the city name and replaces "SAINT" with "ST.".
     * @param {string} value - The city name.
     */
    set city(value) {
        this._city = this.replaceSaintWithSt(value?.toUpperCase());
    }

    /**
     * Gets the mailing city name.
     * @returns {string} The mailing city name.
     */
    get mailCity() {
        return this._mailCity;
    }

    /**
     * Sets the mailing city name and replaces "SAINT" with "ST.".
     * @param {string} value - The mailing city name.
     */
    set mailCity(value) {
        this._mailCity = this.replaceSaintWithSt(value?.toUpperCase());
    }

    /**
     * Gets the county name.
     * @returns {string} The county name.
     */
    get countyName() {
        return this._countyName;
    }

    /**
     * Sets the county name.
     * @param {string} value - The county name.
     */
    set countyName(value) {
        this._countyName = value?.toUpperCase();
    }

    /**
     * Gets the mail county name.
     * @returns {string} The mail county name.
     */
    get mailCountyName() {
        return this._mailCountyName;
    }

    /**
     * Sets the mail county name.
     * @param {string} value - The mail county name.
     */
    set mailCountyName(value) {
        this._mailCountyName = value?.toUpperCase();
    }

    /**
     * Gets the active state of the organization.
     * @returns {string} "Active" if the organization is active, otherwise "Inactive".
     */
    get activeState() {
        return this.isActive ? 'Active' : 'Inactive';
    }

    /**
     * Sets the other county name.
     * @param {string} value - The other county name.
     */
    set otherCountyName(value) {
        this._otherCountyName = value?.toUpperCase();
    }

    /**
     * Gets the other county name.
     * @returns {string} The other county name.
     */
    get otherCountyName() {
        return this._otherCountyName;
    }

    /**
     * Sets the mail other county name.
     * @param {string} value - The other county name.
     */
    set mailOtherCountyName(value) {
        this._mailOtherCountyName = value?.toUpperCase();
    }

    /**
     * Gets the mail other county name.
     * @returns {string} The other county name.
     */
    get mailOtherCountyName() {
        return this._mailOtherCountyName;
    }

    /**
     * Replaces "SAINT" with "ST." in a given string.
     * @param {string} value - The string to process.
     * @returns {string} The processed string.
     */
    replaceSaintWithSt(value) {
        if (!value) return '';
        return value?.replace(/SAINT/g, 'ST.');
    }

    /**
     * Sets the mailing address fields to match the physical address fields.
     */
    setMailingAddressToPhysical() {
        this.mailAddress1 = this.address1;
        this.mailAddress2 = this.address2;
        this.mailCity = this.city;
        this.mailStateProvince = this.stateProvince;
        this.mailPostalCode = this.postalCode;
        this.mailCountyId = this.countyId;
        this.mailCountyName = this.countyName;
        this.mailOtherCountyName = this.otherCountyName;
    }

    /**
     * Sets the physical address fields to match the parent organization's address fields.
     * @param {Object} parent - The parent organization object.
     */
    setPhysicalAddressToParent(parent) {
        this.address1 = parent.address1;
        this.address2 = parent.address2;
        this.city = parent.city;
        this.stateProvince = parent.stateProvince;
        this.countyId = parent.countyId;
        this.countyName = parent.countyName;
        this.otherCountyName = parent.otherCountyName;
        this.postalCode = parent.postalCode;
    }

    /**
     * Populates the model with data from an external source.
     * @param {Object} data - The data object to populate the model.
     */
    setData(data) {
        this.organizationId = data.organizationId;
        this.hccisId = data.hccisId;
        this.parentOrganizationId = data.parentOrganizationId;
        this.parentOrganizationName = data.parentOrganizationName;
        this.organizationTypeId = data.organizationTypeId;
        this.organizationTypeDesc = data.organizationTypeDesc;
        this.name = data.name;
        this.address1 = data.address1;
        this.address2 = data.address2;
        this.city = data.city;
        this.stateProvince = data.stateProvince;
        this.countyId = data.countyId;
        this.countyName = data.countyName;
        this.otherCountyName = data.otherCountyName;
        this.postalCode = formatPostalCode(data.postalCode);
        this.mailAddress1 = data.mailAddress1;
        this.mailAddress2 = data.mailAddress2;
        this.mailCity = data.mailCity;
        this.mailStateProvince = data.mailStateProvince;
        this.mailCountyId = data.mailCountyId;
        this.mailCountyName = data.mailCountyName;
        this.mailOtherCountyName = data.mailOtherCountyName;        
        this.mailPostalCode = formatPostalCode(data.mailPostalCode);
        this.phone = formatPhoneNumber(data.phone);
        this.fax = formatPhoneNumber(data.fax);
        this.openDate = data.openDate ? parseLocalDate(data.openDate) : null;
        this.closeDate = data.closeDate ? parseLocalDate(data.closeDate) : null;
        this.fiscalYearEnd = data.fiscalYearEnd ? parseLocalDate(data.fiscalYearEnd) : null;
        this.webUrl = data.webUrl;
        this.npiNumber = data.npiNumber;
        this.naicNumber = data.naicNumber;
        this.groupCode = data.groupCode;
        this.taxId = data.taxId;
        this.status = data.status;
        this.isActive = data.isActive;  
        this.isInternal = data.isInternal;
        this.isNew = false;
        this.isAffiliate = data.organizationId == data.parentOrganizationId;
        this.initialFiscalYearEnd = data.fiscalYearEnd ? parseLocalDate(data.fiscalYearEnd) : null;
    }

    toJSON() {
        return {
            organizationId: this.organizationId,
            hccisId: this.hccisId,
            parentOrganizationId: this.parentOrganizationId,
            organizationTypeId: this.organizationTypeId,
            name: this.name,
            address1: this.address1,
            address2: this.address2,
            city: this.city,
            stateProvince: this.stateProvince,
            countyId: this.countyId,
            otherCountyName: this.otherCountyName,
            postalCode: this.sanitizeField(this.postalCode),
            mailAddress1: this.mailAddress1,
            mailAddress2: this.mailAddress2,
            mailCity: this.mailCity,
            mailStateProvince: this.mailStateProvince,
            mailCountyId: this.mailCountyId,
            mailOtherCountyName: this.mailOtherCountyName,
            mailPostalCode: this.sanitizeField(this.mailPostalCode),
            phone: this.sanitizeField(this.phone),
            phoneExtension: this.phoneExtension,
            fax: this.sanitizeField(this.fax),
            openDate: this.openDate,
            closeDate: this.closeDate,
            fiscalYearEnd: this.fiscalYearEnd,
            webUrl: this.webUrl,
            groupCode: this.groupCode,
            taxId: this.taxId,
            npiNumber: this.npiNumber,
            naicNumber: this.naicNumber,
            isActive: this.isActive,
            isInternal: this.isInternal
        };
    }

    /**
     * Save an explanation for a fiscal year end change, if there is one.
     * @param {string} explanation - The explanation text.
     * @returns {Promise<void>} A promise that resolves when the explanation is added.
     * @throws {Error} If the explanation is empty or invalid.
     * @description This method inserts a note into the organization's records with the provided explanation.
     */
    async saveFiscalYearEndExplanation() {
        if (!this.fiscalYearEndChangeExplanation || this.fiscalYearEndChangeExplanation.trim() === '') {
            return;
        }

        const noteJSON = {
            organizationId: this.organizationId,
            noteText: `Fiscal Year End Change Explanation: ${this.fiscalYearEndChangeExplanation}`,
        };

        insertOrganizationNote(noteJSON);
    }
    /**
     * Saves the organization data by either inserting or updating it.
     * @returns {Promise<Object>} The result of the save operation.
     */
    async save() {
        this.updateMaskedFields();
        if (this.isNew) {
            return await insertOrganization(this.toJSON());
        } else {
            await this.saveFiscalYearEndExplanation();    
            return await updateOrganization(this.toJSON());
        }
    }

    /**
    * Sanitizes a field by removing non-numeric characters.
    * @param {string|null} field - The field to sanitize.
    * @returns {string|null} The sanitized field.
    */
    sanitizeField(field) {
        return field ? sanitizeNumber(field) : field;
    }

    /**
     * Updates fields that require sanitization (e.g., phone numbers, postal codes).
     */
    updateMaskedFields() {
        this.phone = this.sanitizeField(this.phone);
        this.fax = this.sanitizeField(this.fax);
        this.postalCode = this.sanitizeField(this.postalCode);
        this.mailPostalCode = this.sanitizeField(this.mailPostalCode);
    }

    /**
     * Gets the validation rules for the organization model.
     * @returns {Object} The validation rules.
     */
    static getValidationRules() {
        return { 
            organizationTypeId: { required },
            parentOrganizationId: { required },
            name: { required, maxLength: maxLength(255) },
            address1: { required: helpers.withMessage('Value is required.', requiredValidators['address1']), maxLength: maxLength(120) },
            address2: { required: helpers.withMessage('Value is required.', requiredValidators['address2']), maxLength: maxLength(120) },
            city: { required: helpers.withMessage('Value is required.', requiredValidators['city']), maxLength: maxLength(50) },
            countyId: { required: helpers.withMessage('Value is required', requiredValidators['countyId']) },
            countyName: '',
            otherCountyName: { required: helpers.withMessage('Value is required', otherCountyRequired), maxLength: maxLength(120) },
            stateProvince: { required: helpers.withMessage('Value is required.', requiredValidators['stateProvince']) },
            postalCode: { required: helpers.withMessage('Value is required.', requiredValidators['postalCode']) },
            mailAddress1: { required: helpers.withMessage('Value is required.', requiredValidators['mailAddress1']), maxLength: maxLength(120) },
            mailAddress2: { required: helpers.withMessage('Value is required.', requiredValidators['mailAddress2']), maxLength: maxLength(120) },
            mailCity: { required: helpers.withMessage('Value is required.', requiredValidators['mailCity']), maxLength: maxLength(50) },
            mailStateProvince: { required: helpers.withMessage('Value is required.', requiredValidators['mailStateProvince']) },
            mailPostalCode: { required: helpers.withMessage('Value is required.', requiredValidators['mailPostalCode'])},
            mailCountyId: '',
            mailCountyName: '',
            mailOtherCountyName: { maxLength: maxLength(120) },
            phone: { required: helpers.withMessage('Value is required', requiredValidators['phone']) },
            phoneExtension: { numeric },
            fax: { required: helpers.withMessage('Value is required', requiredValidators['fax']) },
            openDate: '',
            closeDate: '',
            taxId: { required: helpers.withMessage('Value is required', requiredValidators['taxId']), maxLength: maxLength(15)},
            fiscalYearEnd: { required: helpers.withMessage('Value is required', requiredValidators['fiscalYearEnd']) },
            webUrl: { required: helpers.withMessage('Value is required', requiredValidators['webUrl']), validUrl: helpers.withMessage('Invalid web URL', validUrl ), maxLength: maxLength(120) },
            npiNumber: { required: helpers.withMessage('Value is required', requiredValidators['npiNumber']), numeric, minLength: helpers.withMessage('NPI number must be 10 digits', minLength(10)), maxLength: helpers.withMessage('NPI number must be 10 digits' ,maxLength(10)) },
            naicNumber: { required: helpers.withMessage('Value is required', requiredValidators['naicNumber']), numeric, minLength: minLength(5), maxLength: maxLength(5) },
            isActive: { required },
            groupCode: { required: helpers.withMessage('Value is required.', requiredValidators['groupCode'])  ,maxLength: maxLength(10) },
            fiscalYearEndChangeExplanation: '',
       };
    }


    /**
     * Fetches organization data by ID and populates the model.
     * @param {number} organizationId - The ID of the organization to fetch.
     * @returns {Promise<void>} A promise that resolves when the data is fetched.
     */
    async fetch(organizationId) {
        const data = await getOrganization(organizationId, this.userId);
        this.setData(data);
    }
}
